import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'

interface OperatorReport {
  id?: number
  operatorCode?: string
  operatorName?: string
  reportStatus?: string
  reportTime?: string
  reportId?: string
  reportComment?: string
  rejectReason?: string
  createdAt?: string
  updatedAt?: string
  reportStatusText?: string
  operatorDisplayName?: string
}

interface OperatorReportStatusDisplayProps {
  operatorReports?: OperatorReport[]
  compact?: boolean // 是否使用紧凑模式（用于表格）
}

const operatorMap: Record<string, { name: string; color: string; icon: string }> = {
  CMCC: { name: '中国移动', color: '#E60012', icon: '📞' },
  CUCC: { name: '中国联通', color: '#E60012', icon: '📞' },
  CTCC: { name: '中国电信', color: '#E60012', icon: '📞' },
}

const statusMap: Record<string, { label: string; variant: 'secondary' | 'warning' | 'success' | 'destructive'; icon: any; color: string }> = {
  NOT_REPORTED: { label: '未报备', variant: 'secondary', icon: AlertCircle, color: 'text-gray-600' },
  REPORTING: { label: '报备中', variant: 'warning', icon: Clock, color: 'text-yellow-600' },
  REPORTED: { label: '已报备', variant: 'success', icon: CheckCircle, color: 'text-green-600' },
  REJECTED: { label: '报备被拒', variant: 'destructive', icon: XCircle, color: 'text-red-600' },
}

export function OperatorReportStatusDisplay({ operatorReports, compact = false }: OperatorReportStatusDisplayProps) {
  if (!operatorReports || operatorReports.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {compact ? '无报备信息' : '暂无运营商报备信息'}
      </div>
    )
  }

  if (compact) {
    // 紧凑模式：用于表格，显示为垂直列表
    return (
      <div className="space-y-1">
        {operatorReports.map((report) => {
          const operator = operatorMap[report.operatorCode || '']
          const status = statusMap[report.reportStatus || '']
          const Icon = status?.icon || AlertCircle
          
          return (
            <div key={report.id} className="flex items-center justify-between text-xs">
              <span className="text-gray-700">{operator?.name || report.operatorName || '未知运营商'}</span>
              <div className="flex items-center space-x-1">
                <Badge variant={status?.variant || 'secondary'} className="text-xs flex items-center gap-1">
                  <Icon className="h-3 w-3" />
                  {status?.label || '未知'}
                </Badge>
                {report.reportStatus === 'REPORTING' && (
                  <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // 完整模式：用于详情页面，显示更多信息
  return (
    <div className="space-y-2">
      {operatorReports.map((report) => {
        const operator = operatorMap[report.operatorCode || '']
        const status = statusMap[report.reportStatus || '']
        const Icon = status?.icon || AlertCircle
        
        return (
          <div key={report.id} className="flex items-center justify-between p-2 border rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="text-sm">{operator?.icon || '📡'}</div>
              <div>
                <div className="font-medium text-sm">{operator?.name || report.operatorName || '未知运营商'}</div>
                <div className="text-xs text-muted-foreground">
                  {report.reportTime ? `报备时间: ${new Date(report.reportTime).toLocaleString()}` : '未报备'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Badge variant={status?.variant || 'secondary'} className="text-xs flex items-center gap-1">
                <Icon className="h-3 w-3" />
                {status?.label || '未知状态'}
              </Badge>
              {report.reportStatus === 'REPORTING' && (
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
